import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import apiClient from '@/lib/axios'
import { Branch } from './useBranches'
import { AxiosError } from 'axios'

/**
 * Hook to fetch branches for a specific restaurant
 * Used by super admin to fetch branches for any restaurant
 * @param restaurantId Restaurant ID to fetch branches for
 */
export function useBranchesForRestaurant(restaurantId: string | null | undefined) {
  const { getToken } = useAuth()

  return useQuery<Branch[]>({
    queryKey: ['branches', 'restaurant', restaurantId],
    queryFn: async () => {
      if (!restaurantId) {
        return []
      }

      try {
        const token = await getToken()
        if (!token) {
          throw new Error('No authentication token available')
        }

        // Use the restaurants/:restaurantId/branches endpoint
        const response = await apiClient.get<Branch[]>(
          `/restaurants/${restaurantId}/branches`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        return response.data
      } catch (error) {
        // If 403 or 404, return empty array
        const axiosError = error as AxiosError
        if (axiosError.response?.status === 403 || axiosError.response?.status === 404) {
          return []
        }
        throw error
      }
    },
    enabled: !!restaurantId, // Only fetch if restaurantId is provided
    staleTime: 300000, // 5 minutes
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry on 403/404 errors
      const axiosError = error as AxiosError
      if (axiosError?.response?.status === 403 || axiosError?.response?.status === 404) {
        return false
      }
      return failureCount < 2
    },
  })
}

